import {produce} from 'immer'
import * as React from 'react'
import {GQLHanmiManager, GQLHanmiManagerLoginInput} from '@mdpp/common/lib/@types/server.schema'
import {AppOptions, I_ADMIN_OPTIONS, LOCAL_STORAGE_KEY_TOKEN} from '../../AppOptions'
import {useHanmiManagerAPIs} from "./useHanmiManagerAPIs";

interface IHanmiManagerAppContextType {
  state: IHanmiManagerAppReducerState
  sideBarState: number
  login: (input: GQLHanmiManagerLoginInput) => Promise<GQLHanmiManager>
  logout: () => Promise<void>
  refreshProfile: () => Promise<GQLHanmiManager>
  refreshSidebar: () => void
}

export enum LOGIN_STATE {
  Checking = 'Checking',
  LoggedOut = 'LoggedOut',
  LoggingIn = 'LoggingIn',
  LoggedIn = 'LoggedIn',
  LoggingOut = 'LoggingOut',
}

interface IHanmiManagerAppContextProviderProps {}

interface IHanmiManagerAppReducerState {
  loginState: LOGIN_STATE
  profile: GQLHanmiManager | null
}

enum HanmiManagerAppActionType {
  SetLoggedIn = 'HanmiManager/SetLoggedIn',
  SetLoggedOut = 'HanmiManager/SetLoggedOut',
  SetAppConfig = 'HanmiManager/SetAppConfig',
  SetNodeClick = 'HanmiManager/SetNodeClick', // whenever directory view's node is clicked, count++
}

interface IHanmiManagerAppActionSetLoggedIn {
  type: HanmiManagerAppActionType.SetLoggedIn
  profile: GQLHanmiManager
}

interface IHanmiManagerAppActionSetLoggedOut {
  type: HanmiManagerAppActionType.SetLoggedOut
}

type HanmiManagerAppReducerAction = IHanmiManagerAppActionSetLoggedIn | IHanmiManagerAppActionSetLoggedOut

interface IHanmiManagerAppReducer extends React.Reducer<IHanmiManagerAppReducerState, HanmiManagerAppReducerAction> {}

function createAppReducer(): IHanmiManagerAppReducer {
  return function (state: IHanmiManagerAppReducerState, action: HanmiManagerAppReducerAction): IHanmiManagerAppReducerState {
    return produce(state, draft => {
      switch (action.type) {
        case HanmiManagerAppActionType.SetLoggedIn:
          draft.loginState = LOGIN_STATE.LoggedIn
          draft.profile = action.profile
          break
        case HanmiManagerAppActionType.SetLoggedOut:
          draft.loginState = LOGIN_STATE.LoggedOut
          break
        default:
          return
      }
    })
  }
}

const actionSetLoggedIn = (profile: GQLHanmiManager): IHanmiManagerAppActionSetLoggedIn => ({
  type: HanmiManagerAppActionType.SetLoggedIn,
  profile,
})
const actionSetLoggedOut = (): IHanmiManagerAppActionSetLoggedOut => ({ type: HanmiManagerAppActionType.SetLoggedOut })

function createInitialAppReducerState(): IHanmiManagerAppReducerState {
  return {
    loginState: LOGIN_STATE.Checking,
    profile: null,
  }
}

function createHanmiManagerAppContext(appOptions: I_ADMIN_OPTIONS) {
  const Context: React.Context<IHanmiManagerAppContextType> = React.createContext<IHanmiManagerAppContextType>(
    // tslint:disable-next-line
    {} as IHanmiManagerAppContextType
  )

  const DoctorProvider: React.FC<IHanmiManagerAppContextProviderProps> = props => {
    const { children } = props

    const { loginHanmiManager: apiLogin, getHanmiManagerProfile: apiGetProfile, logout: apiLogout } = useHanmiManagerAPIs()

    const [sideBarState, setSideBarState] = React.useState(0)
    const [state, dispatch] = React.useReducer<IHanmiManagerAppReducer>(createAppReducer(), createInitialAppReducerState())
    const setLoggedIn = React.useCallback((profile: GQLHanmiManager) => dispatch(actionSetLoggedIn(profile)), [dispatch])

    const checkLogin = React.useCallback(async () => {
      try {
        // const appConfig = await apiGetAppConfig()
        // dispatch(actionSetAppConfig(appConfig))

        const profile = await apiGetProfile()
        setLoggedIn(profile)
      } catch (ex) {
        // 로그인 실패, 로그아웃된 상태
        dispatch(actionSetLoggedOut())
      }
    }, [apiGetProfile, setLoggedIn])

    const login = React.useCallback(
      async (data: GQLHanmiManagerLoginInput): Promise<GQLHanmiManager> => {
        const res = await apiLogin(data)
        localStorage.setItem(LOCAL_STORAGE_KEY_TOKEN, res.token)
        setLoggedIn(res.me)

        return res.me
      },
      [apiLogin, setLoggedIn]
    )

    const refreshSidebar = () => {
      if (sideBarState === 0) {
        setSideBarState(1)
      } else {
        setSideBarState(0)
      }
    }

    const logout = React.useCallback(async () => {
      await apiLogout()
      const managerEmail = localStorage.getItem('managerEmail')
      const doctorEmail = localStorage.getItem('doctorEmail')
      const adminEmail = localStorage.getItem('adminEmail')
      const pharmacyEmail = localStorage.getItem('pharmacyEmail')
      const hanmiManagerEmail = localStorage.getItem('hanmiManagerEmail')
      localStorage.clear()
      managerEmail ? localStorage.setItem('managerEmail', managerEmail) : console.log('not remember manager !')
      doctorEmail ? localStorage.setItem('doctorEmail', doctorEmail) : console.log('not remember doctorEmail !')
      adminEmail ? localStorage.setItem('adminEmail', adminEmail) : console.log('not remember adminEmail !')
      pharmacyEmail ? localStorage.setItem('pharmacyEmail', pharmacyEmail) : console.log('not remember pharmacyEmail !')
      hanmiManagerEmail ? localStorage.setItem('hanmiManagerEmail', hanmiManagerEmail) : console.log('not remember hanmiManagerEmail !')
      dispatch(actionSetLoggedOut())
    }, [apiLogout])

    const refreshProfile = React.useCallback(async () => {
      const profile = await apiGetProfile()
      setLoggedIn(profile)
      return profile
    }, [apiGetProfile, setLoggedIn])

    React.useEffect(() => {
      // noinspection JSIgnoredPromiseFromCall
      checkLogin()
    }, [checkLogin])

    const context: IHanmiManagerAppContextType = {
      state,
      sideBarState,
      login,
      logout,
      refreshProfile,
      refreshSidebar,
    }
    return <Context.Provider value={context}>{children}</Context.Provider>
  }

  return { Context, Provider: DoctorProvider }
}

const { Context: AppContext, Provider } = createHanmiManagerAppContext(AppOptions)

export const HanmiManagerAppProvider = Provider
export const HanmiManagerAppContext = AppContext

export function useHanmiManagerAppContext(): IHanmiManagerAppContextType {
  return React.useContext(AppContext)
}
